/**
 * TrustExplainerPage — iter178.
 *
 * Public marketing page at /trust that explains what the "Verified author"
 * badge means. Linked from the badge's "Learn more" link. Acts as a passive
 * acquisition surface: readers who see verified content on the web click
 * through and learn about dotbox's anti-abuse posture.
 *
 * No auth required. No API calls. Pure content page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, MailCheck, Bot, UserX, Sparkles, ArrowRight, Users } from 'lucide-react';
import DotboxLogo from '@/components/common/DotboxLogo';

const CHECKS = [
  {
    icon: Bot,
    title: 'Real human, not a bot',
    body: 'Every account passes a Cloudflare Turnstile bot-check when it signs up. Automated signup attempts are rejected before an account is ever created.',
  },
  {
    icon: MailCheck,
    title: 'Real, verified email',
    body: 'The publisher clicked a one-time verification link sent to their email inbox. Disposable / throwaway domains are blocked at signup — no `10-minute-mail`, no burner domains.',
  },
  {
    icon: UserX,
    title: 'Not flagged for abuse',
    body: "If an admin ever flags the publisher's account, the Verified badge disappears from all their public pages within seconds — no cleanup job required.",
  },
];

const TrustExplainerPage = () => {
  return (
    <div className="min-h-screen bg-[#0A1024] text-slate-100" data-testid="trust-page">
      {/* Header */}
      <header className="border-b border-[#1F2A4D] bg-[#0F1730]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="trust-logo-link">
            <DotboxLogo className="h-7" showText={true} variant="dark" />
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 font-semibold"
            data-testid="trust-cta-signup"
          >
            Sign up for dotbox
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-6">
          <ShieldCheck className="h-8 w-8 text-emerald-400" />
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
          The <span className="text-emerald-400">Verified author</span> badge, explained
        </h1>
        <p className="text-slate-400 text-base leading-relaxed max-w-2xl mx-auto">
          When you see a green &ldquo;Verified author&rdquo; badge on a page shared through dotbox,
          it means a real human — not a bot farm — is behind that account. Here&apos;s exactly
          what that badge does (and doesn&apos;t) certify.
        </p>
      </section>

      {/* Checks grid */}
      <section className="max-w-4xl mx-auto px-4 pb-16">
        <h2 className="text-lg font-semibold mb-6 text-slate-200 text-center">
          Every Verified author has cleared these three checks
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CHECKS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-xl border border-[#1F2A4D] bg-[#0F1730] p-6"
              data-testid={`trust-check-${title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 mb-3">
                <Icon className="h-5 w-5 text-emerald-400" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* iter179 — Two-tier explainer: Verified vs Team-verified */}
      <section className="max-w-4xl mx-auto px-4 pb-16" data-testid="trust-tiers-section">
        <h2 className="text-lg font-semibold mb-6 text-slate-200 text-center">
          Two tiers of trust
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Verified tier card */}
          <div
            className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-6"
            data-testid="trust-tier-verified"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-semibold">
                <ShieldCheck className="h-3 w-3" />
                Verified author
              </span>
            </div>
            <h3 className="text-base font-semibold text-white mb-2">
              Individual verified account
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              A real human owner who&apos;s cleared the three anti-abuse checks above.
              This is the default badge every dotbox publisher earns when they finish
              signing up.
            </p>
          </div>
          {/* Team-verified tier card */}
          <div
            className="rounded-xl border border-amber-500/50 bg-amber-500/5 p-6"
            data-testid="trust-tier-team"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/50 text-amber-300 text-xs font-semibold">
                <Users className="h-3 w-3" />
                Team-verified
              </span>
            </div>
            <h3 className="text-base font-semibold text-white mb-2">
              Team account on an active paid plan
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Everything the green badge means, <strong>plus</strong> the publisher is
              a member of a team account whose owner has an active paid subscription —
              a stronger signal for professional and organisational publishers.
            </p>
          </div>
        </div>
      </section>

      {/* What it doesn't mean */}
      <section className="max-w-3xl mx-auto px-4 pb-16">
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6" data-testid="trust-doesnt-mean">
          <h2 className="text-lg font-semibold text-amber-300 mb-3">
            What the badge does <em>not</em> mean
          </h2>
          <ul className="text-sm text-slate-300 space-y-2 leading-relaxed">
            <li className="flex items-start gap-2">
              <span className="text-amber-300 mt-0.5">•</span>
              It does <strong>not</strong> certify that the content is factually true.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-300 mt-0.5">•</span>
              It does <strong>not</strong> mean the publisher is an expert or an authority on the topic.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-300 mt-0.5">•</span>
              It does <strong>not</strong> imply endorsement by dotbox. We verify identity, not opinions.
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Think of it like a driver&apos;s license photo: it proves who&apos;s behind the wheel,
            not that they&apos;re a good driver.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-4 pb-20 text-center">
        <div className="rounded-xl border border-[#1F2A4D] bg-gradient-to-br from-[#0F1730] to-[#0A1024] p-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 mb-4">
            <Sparkles className="h-6 w-6 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Publish content your readers can trust</h2>
          <p className="text-slate-400 text-sm mb-6 max-w-lg mx-auto">
            Every share you publish through dotbox automatically carries the Verified author
            badge once your email is confirmed. No extra setup, no gatekeepers.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold shadow-lg shadow-red-900/30 transition-colors"
            data-testid="trust-cta-bottom"
          >
            Get started for free
            <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="text-xs text-slate-500 mt-4">
            7-day free trial &middot; No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1F2A4D] bg-[#0F1730]">
        <div className="max-w-5xl mx-auto px-4 py-6 text-center text-xs text-slate-500">
          &copy; 2026 dotbox.ai &middot;{' '}
          <Link to="/" className="hover:text-slate-300">Home</Link> &middot;{' '}
          <Link to="/contact" className="hover:text-slate-300">Contact</Link>
        </div>
      </footer>
    </div>
  );
};

export default TrustExplainerPage;
